import React, { useCallback, useEffect, useState } from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import ProviderSettingsPanel from '@devnw/cyber-iam/identity/ProviderSettingsPanel/ProviderSettingsPanel'
import type {
  ProviderField,
} from '@devnw/cyber-iam/iam/types'
import { PamLoading, PamError } from '../components/PamStateComponents'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import type {
  AdminPutMailProviderReq,
  AdminMailProviderResp,
} from '../../../gen/client/react/client'

/**
 * TenantMailProvider — per-tenant mail-provider configuration admin page
 * (Mail Task 8, tenant-secret-mail-providers plan).
 *
 * OIDCPAM Gate-6 Phase 6c: migrated off MUI onto
 * @devnw/cyber-iam/identity/ProviderSettingsPanel — the SAME schema-driven
 * component TenantSecretsProvider.tsx uses, supplied with the MAIL field
 * schema below. The component owns the write-only credential input (never
 * seeded from `config`, no matter what the GET response carries), the
 * env-locked read-only projection (structurally no editable/password input
 * in that branch), and the delete two-phase confirm. This page owns every
 * API call (adminGetMailProvider / adminPutMailProvider /
 * adminDeleteMailProvider), the tenant/routing/404-vs-error shell, and the
 * wire-mapping below.
 *
 * WIRE MAPPING: the generated client's decodeJSON()/fromWire() already
 * snake_case -> camelCase converts the response, so AdminMailProviderResp
 * is ALREADY camelCase at this boundary (host, port, tlsMode, fromAddr,
 * username, credentialSet, envLocked, tenantId, createdAt, updatedAt). The
 * `fields` schema below names each editable field to MATCH those response
 * keys 1:1 (host/port/tlsMode/fromAddr/username/credential) — so
 * `toMailConfig` is a pure passthrough with zero snake<->camel translation,
 * and `handleSave` reads `values.<sameName>` straight into
 * AdminPutMailProviderReq with no renaming either. This is deliberately
 * different from the pre-migration MUI page, whose local `Field[]` schema
 * used snake_case names (`tls_mode`, `from_addr`) purely for its own form
 * state keys, disconnected from the client's actual camelCase field names —
 * a naming mismatch that invites exactly the "read the wrong key off an
 * already-camelCase object" bug class this migration guards against
 * elsewhere (see MtlsCAs.tsx's latent-bug note).
 *
 * Env-lock: admin_mail_provider_server.go's AdminGetMailProvider ALWAYS
 * returns 200 for the top-level/default tenant (config.TenantID) — a live
 * projection of the env-configured (THREADID_SMTP_*) provider with
 * env_locked:true — and its AdminPutMailProvider/AdminDeleteMailProvider
 * fail closed with 400 for that tenant regardless of caller permissions.
 * ProviderSettingsPanel renders that response as a read-only field list with
 * no editable credential input and disabled Save/Delete, so the UI never
 * even offers a mutation the server would reject. Every other tenant's GET
 * returns 404 until first configured; that is NOT an error — `config` stays
 * `null` and the component renders its own "not configured yet" copy and a
 * ready-to-fill form.
 */

const TLS_MODE_OPTIONS = [
  // starttls listed first so an unconfigured tenant's empty-seeded <select>
  // (no field-level default in ProviderField) falls back to it, matching
  // the pre-migration page's `cfg?.tlsMode || 'starttls'` default.
  { value: 'starttls', label: 'STARTTLS' },
  { value: 'implicit', label: 'Implicit TLS' },
]

const mailProviderFields: ProviderField[] = [
  { name: 'host', label: 'Host', type: 'text', required: true },
  { name: 'port', label: 'Port', type: 'text' },
  {
    name: 'tlsMode',
    label: 'TLS Mode',
    type: 'select',
    options: TLS_MODE_OPTIONS,
    required: true,
  },
  { name: 'fromAddr', label: 'From Address', type: 'text' },
  { name: 'username', label: 'Username', type: 'text' },
  { name: 'credential', label: 'Credential', type: 'password' },
]

/**
 * Extracts a human-readable message from a rejection. Duck-types on a string
 * `message` property rather than requiring `instanceof Error` — the real
 * generated-client HTTPError IS an Error subclass so this still reads its
 * resolved message, but the check also works for any plain error-shaped
 * object, matching httpStatus's "read fields directly for reliability" below.
 */
function errMessage(e: unknown): string {
  if (e !== null && typeof e === 'object' && 'message' in e) {
    const m = (e as { message: unknown }).message
    if (typeof m === 'string' && m) return m
  }
  return 'Request failed'
}

/**
 * Extracts the HTTP status from a generated-client HTTPError WITHOUT an
 * `instanceof` check, matching GovernanceRoleRegistry.tsx's
 * friendlyApiError precedent ("read .status directly for reliability").
 * Works for both the real HTTPError class and a plain mocked error object.
 */
function httpStatus(e: unknown): number {
  if (e !== null && typeof e === 'object' && 'status' in e) {
    const s = (e as { status: unknown }).status
    if (typeof s === 'number') return s
  }
  return 0
}

/** AdminMailProviderResp -> ProviderSettingsPanel's `config` prop — a pure
 *  field-name passthrough (see the file header's wire-mapping note). The
 *  write-only `credential` is deliberately absent: the response never
 *  carries it, and the component never seeds a password field from `config`
 *  regardless. */
function toMailConfig(resp: AdminMailProviderResp): Record<string, unknown> {
  return {
    host: resp.host,
    port: resp.port,
    tlsMode: resp.tlsMode,
    fromAddr: resp.fromAddr,
    username: resp.username,
  }
}

export const TenantMailProvider = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [config, setConfig] = useState<AdminMailProviderResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!tenant) return undefined
    let cancelled = false
    setLoading(true)
    setError(undefined)
    client
      .adminGetMailProvider({ tenantId: tenant })
      .then((resp) => {
        if (cancelled) return
        setConfig(resp)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        if (httpStatus(e) === 404) {
          // Not an error — this tenant simply has no stored config yet.
          setConfig(null)
        } else {
          setError(errMessage(e))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, tenant])

  const handleSave = useCallback(
    async (values: Record<string, unknown>): Promise<void> => {
      const portRaw =
        typeof values.port === 'string' ? values.port.trim() : values.port
      const req: AdminPutMailProviderReq = {
        tenantId: tenant,
        host: String(values.host ?? ''),
        port:
          portRaw !== undefined && portRaw !== null && portRaw !== ''
            ? Number(portRaw)
            : undefined,
        tlsMode: String(values.tlsMode || 'starttls'),
        fromAddr: String(values.fromAddr ?? ''),
        username: String(values.username ?? ''),
        // Write-only + leave-blank-to-keep-unchanged: only send a
        // credential when the admin actually typed one (mirrors
        // Connectors.tsx's client_secret handling). ProviderSettingsPanel
        // already omits this key from `values` entirely when left blank.
        credential: values.credential
          ? String(values.credential)
          : undefined,
      }
      const resp = await client.adminPutMailProvider(req)
      setConfig(resp)
    },
    [client, tenant]
  )

  const handleDelete = useCallback(async (): Promise<void> => {
    await client.adminDeleteMailProvider({ tenantId: tenant })
    setConfig(null)
  }, [client, tenant])

  // The initial GET's loading/error states are handled as page-level early
  // returns (StepUpPolicy.tsx precedent) rather than passed into
  // ProviderSettingsPanel's own `loading`/`error` props: the component's
  // `config === null` branch unconditionally renders its own "not
  // configured yet" copy, which would otherwise render ALONGSIDE a genuine
  // (non-404) fetch-error banner — `config` stays `null` in both the
  // not-yet-configured AND the real-error case in this page's state model.
  // Gating here keeps "not configured" and "load failed" mutually exclusive.
  if (loading) return <PamLoading />
  if (error) return <PamError message={error} />

  return (
    <ProviderSettingsPanel
      testID="mail-provider"
      title="Mail Provider"
      description="Outbound mail (SMTP) configuration."
      tenant={tenant}
      fields={mailProviderFields}
      config={config ? toMailConfig(config) : null}
      credentialSet={config?.credentialSet ?? false}
      envLocked={config?.envLocked ?? false}
      onSave={handleSave}
      onDelete={config ? handleDelete : undefined}
    />
  )
}
